import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnakBarTran } from '../../../hook';
import { queryKeys, mutationKeys } from '../keys';
import {
  deleteDocument,
  editDocument,
  submitQuestionaire,
  updateFieldStatus,
  addProducerByPartner,
  addField,
  putFarmerBankStatus
} from './mutations';

// --- useUpdateFieldStatus ---
export const useUpdateFieldStatus = () => {
  const queryClient = useQueryClient();
  const { showSuccessSnackbar, showErrorSnackbar } = useSnakBarTran();

  return useMutation({
    mutationKey: mutationKeys.updateFieldStatus,
    mutationFn: (data) => updateFieldStatus(data),
    onSuccess: (data) => {
      console.log('Status updated successfully', data);
      showSuccessSnackbar({ text: 'Status updated successfully' });
      queryClient.invalidateQueries({ queryKey: queryKeys.fieldById });
    },
    onError: (err) => {
      console.error('Error updating status:', err);
      showErrorSnackbar({ text: err?.response?.data?.message || 'Failed to update status' });
    }
  });
};

// --- useSubmitQuestionaire ---
export const useSubmitQuestionaire = () =>
  useMutation({
    mutationKey: mutationKeys.submitQuestionaire,
    mutationFn: (data) => submitQuestionaire(data),
    onSuccess: (data) => {
      console.log('mutation success', data);
    },
    onError: (err) => {
      console.error('mutation error', err);
    },
  });

// --- useEditDocument ---
export const useEditDocument = () =>
  useMutation({
    mutationKey: mutationKeys.editDocument,
    mutationFn: (data) => editDocument(data),
    onSuccess: (data) => {
      console.log('mutation success', data);
    },
    onError: (err) => {
      console.error('mutation error', err);
    },
  });

// --- useDeleteDocument ---
export const useDeleteDocument = () =>
  useMutation({
    mutationKey: mutationKeys.deleteDocument,
    mutationFn: (data) => deleteDocument(data),
    onSuccess: (data) => {
      console.log('mutation success', data);
    },
    onError: (err) => {
      console.error('mutation error', err);
    },
  });

// --- useAddProducerByPartner ---
export const useAddProducerByPartner = () => {
  const queryClient = useQueryClient();
  const { showSuccessSnackbar, showErrorSnackbar } = useSnakBarTran();

  return useMutation({
    mutationKey: mutationKeys.addProducerByPartner,
    mutationFn: (data) => addProducerByPartner(data),
    onSuccess: (data) => {
      console.log('Producer added successfully', data);
      showSuccessSnackbar({ text: 'Producer added successfully' });
      queryClient.invalidateQueries({ queryKey: queryKeys.allProducers });
    },
    onError: (err) => {
      console.error('Error adding producer:', err);
      showErrorSnackbar({ text: 'Failed to add producer' });
    },
  });
};

// --- useAddField ---
export const useAddField = () => {
  const queryClient = useQueryClient();
  const { showSuccessSnackbar, showErrorSnackbar } = useSnakBarTran();

  return useMutation({
    mutationKey: mutationKeys.addField,
    mutationFn: (data) => addField(data),
    onSuccess: (data) => {
      console.log('Field added successfully', data);
      showSuccessSnackbar({ text: 'Ranch added successfully' });
      queryClient.invalidateQueries({ queryKey: queryKeys.fields }); // make sure queryKeys.fields exists
    },
    onError: (err) => {
      console.error('Error adding field:', err);
      showErrorSnackbar({ text: 'Failed to add ranch' });
    },
  });
};

// --- useEditField ---
export const useEditField = () => {
  const queryClient = useQueryClient();
  const { showSuccessSnackbar, showErrorSnackbar } = useSnakBarTran();

  return useMutation({
    mutationKey: mutationKeys.editField,
    mutationFn: (data) => editDocument(data),
    onSuccess: (data) => {
      console.log('Field edited successfully', data);
      showSuccessSnackbar({ text: 'Ranch edited successfully' });
      queryClient.invalidateQueries({ queryKey: queryKeys.fields }); // make sure queryKeys.fields exists
    },
    onError: (err) => {
      console.error('Error editing field:', err);
      showErrorSnackbar({ text: 'Failed to edit ranch' });
    },
  });
};

export const usePutFarmerBankStatus = () => {
  const { showErrorSnackbar, showSuccessSnackbar } = useSnakBarTran();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: putFarmerBankStatus,
    onSuccess: (data) => {
      console.log('Farmer bank status updated successfully', data);
      queryClient.invalidateQueries({ queryKey: queryKeys.bankFarmers });
    },
    onError: (err) => {
      console.error('Error updating farmer bank status:', err);
    }
  });
};
